import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
var _excluded = ["title", "text", "backgroundColor", "actions", "className", "layout", "onDismiss", "dismissButtonStyle", "desktopOnly", "mobileImageLayout"],
  _excluded2 = ["title", "text", "actions", "className", "onDismiss", "dismissButtonStyle", "desktopOnly", "heroColor"];
import React from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import { XBoldIcon } from '@pattern-library/icon';
var propTypes = {
  /**
   * Basic background color options
   */
  backgroundColor: PropTypes.oneOf(['brand', 'white']),
  /**
   * Additional classes for the banner
   */

  className: PropTypes.string,
  /**
   * The banner's title. If a string is passed, default styles will be applied based on the
   * selected layout. Otherwise, it will be left to the developer to style any JSX elements
   * passed as the title.
   */
  title: PropTypes.node.isRequired,
  /**
   * The banner's text content
   */
  text: PropTypes.node.isRequired,
  /**
   * Actions (buttons or links) to be displayed inside the banner
   */
  actions: PropTypes.node,
  /**
   * The image element that decorates the banner
   */
  image: PropTypes.element,
  /**
   * If provided, a close button will be displayed in the banner's upper right corner,
   * and this callback will be called when it is clicked.
   */
  onDismiss: PropTypes.func,
  /**
   *
   */
  dismissButtonStyle: PropTypes.oneOf(['light', 'dark']),
  /**
   * Defines how the contents of the banner will be laid out on medium and large screens:
   *
   * - **`vertical`** displays the title, text, and actions vertically stacked on the left
   *   side of the banner and the image on the right.
   * - **`horizontal`** displays the title and text vertically stacked on the left with
   *   the actions to the right in front of the image.
   * - **`hero`** makes the title text larger and moves the text content beneath the image.
   */
  layout: PropTypes.oneOf(['vertical', 'horizontal', 'hero', 'fill']),
  /**
   * Layout of the banner in mobile sizes for vertical, horizontal and fill layouts only
   */
  mobileImageLayout: PropTypes.oneOf(['top', 'bottom']),
  /**
   * Defines the transition from text content on the left side of the banner to the image on
   * the right:
   *
   * - **`angle`** uses the angle only
   * - **`angle-mask`** shows a semi-opaque angled divider
   * - **`gradient`** uses a linear transparency gradient
   */
  imageEffect: PropTypes.oneOf(['angle', 'angle-mask', 'gradient']),
  /**
   * If `true`, the image will always be hidden. If one of `small`, `medium`, or `large`, the
   * image will be hidden on screens smaller than or matching that breakpoint. Otherwise, the
   * image will appear below the actions and will use a transparency gradient.
   */
  hideImage: PropTypes.oneOfType([PropTypes.bool, PropTypes.oneOf(['small', 'medium', 'large'])]),
  /**
   * If `true`, the banner will not have responsive styles applied
   */
  desktopOnly: PropTypes.bool,
  /**
   * Color of the text and hero footer for the hero layout
   */
  heroColor: PropTypes.oneOf(['gray-5', 'noble-blue'])
};
var defaultProps = {
  backgroundColor: 'white',
  layout: 'vertical',
  mobileImageLayout: 'bottom',
  imageEffect: 'angle-mask',
  hideImage: false,
  dismissButtonStyle: 'light',
  desktopOnly: false,
  heroColor: 'noble-blue'
};
function MarketingBanner(props) {
  var layout = props.layout;
  if (layout === 'hero') {
    return getHeroContent(props);
  }
  return getBannerContent(props);
}
function getDismiss(onDismiss, dismissButtonStyle) {
  return /*#__PURE__*/React.createElement("button", {
    className: "asc-marketing-banner__dismiss asc-marketing-banner__dismiss--".concat(dismissButtonStyle),
    onClick: onDismiss
  }, /*#__PURE__*/React.createElement(XBoldIcon, {
    color: "current",
    size: "xs",
    label: "Close"
  }));
}
function getBackground(_ref) {
  var image = _ref.image,
    imageEffect = _ref.imageEffect,
    hideImage = _ref.hideImage;
  return /*#__PURE__*/React.createElement("div", {
    className: cx('asc-marketing-banner__image-container', "asc-marketing-banner__image-container--".concat(imageEffect), hideImage === true && 'asc-marketing-banner__image-container--hidden', typeof hideImage === 'string' && "asc-marketing-banner__image-container--hidden-".concat(hideImage))
  }, image && /*#__PURE__*/React.cloneElement(image, {
    className: cx('asc-marketing-banner__background-image', image.props.className)
  }));
}
function getBannerContent(_ref2) {
  var title = _ref2.title,
    text = _ref2.text,
    backgroundColor = _ref2.backgroundColor,
    actions = _ref2.actions,
    className = _ref2.className,
    layout = _ref2.layout,
    onDismiss = _ref2.onDismiss,
    dismissButtonStyle = _ref2.dismissButtonStyle,
    desktopOnly = _ref2.desktopOnly,
    mobileImageLayout = _ref2.mobileImageLayout,
    props = _objectWithoutProperties(_ref2, _excluded);
  return /*#__PURE__*/React.createElement("div", {
    className: cx(className, 'asc-marketing-banner', "asc-marketing-banner--".concat(layout), "asc-marketing-banner--bg-".concat(backgroundColor), "asc-marketing-banner--mobile-layout-".concat(mobileImageLayout), desktopOnly && 'asc-marketing-banner--desktop-only')
  }, getBackground(props), /*#__PURE__*/React.createElement("div", {
    className: "asc-marketing-banner__content"
  }, /*#__PURE__*/React.createElement("div", {
    className: "asc-marketing-banner__text-content"
  }, /*#__PURE__*/React.createElement("div", {
    className: cx('asc-marketing-banner__title', typeof title === 'string' && 'asc-marketing-banner__title--heading')
  }, title), /*#__PURE__*/React.createElement("div", {
    className: "asc-marketing-banner__text"
  }, text)), actions && /*#__PURE__*/React.createElement("div", {
    className: "asc-marketing-banner__actions"
  }, actions)), onDismiss && getDismiss(onDismiss, dismissButtonStyle));
}
function getHeroContent(_ref3) {
  var title = _ref3.title,
    text = _ref3.text,
    actions = _ref3.actions,
    className = _ref3.className,
    onDismiss = _ref3.onDismiss,
    dismissButtonStyle = _ref3.dismissButtonStyle,
    desktopOnly = _ref3.desktopOnly,
    heroColor = _ref3.heroColor,
    props = _objectWithoutProperties(_ref3, _excluded2);
  return /*#__PURE__*/React.createElement("div", {
    className: cx(className, 'asc-marketing-banner', 'asc-marketing-banner--hero', desktopOnly && 'asc-marketing-banner--desktop-only', "asc-marketing-banner--hero-".concat(heroColor))
  }, /*#__PURE__*/React.createElement("div", {
    className: "asc-marketing-banner__content"
  }, getBackground(props), /*#__PURE__*/React.createElement("div", {
    className: cx('asc-marketing-banner__title', typeof title === 'string' && 'asc-marketing-banner__title--heading')
  }, title)), /*#__PURE__*/React.createElement("div", {
    className: "asc-marketing-banner__hero-footer"
  }, /*#__PURE__*/React.createElement("div", {
    className: "asc-marketing-banner__text"
  }, text), actions && /*#__PURE__*/React.createElement("div", {
    className: "asc-marketing-banner__actions"
  }, actions)), onDismiss && getDismiss(onDismiss, dismissButtonStyle));
}
MarketingBanner.propTypes = propTypes;
MarketingBanner.defaultProps = defaultProps;
export default MarketingBanner;